export interface PostHogFeatureFlagDefinition {
  key: string;
  defaultValue: boolean;
  description?: string;
}

export const POSTHOG_FEATURE_FLAG_DEFINITIONS = {
  IS_OL_APPOINTMENTS_ENABLED: {
    key: "is-ol-appointments-enabled",
    defaultValue: true,
    description:
      "Toggles between the legacy appointment scheduler and OpenLoop onboarding.",
  },
  USE_NEW_APP_STORE_LINKS: {
    key: "use-new-app-store-links",
    defaultValue: false,
    description:
      "Controls whether to deep link to new app store landing pages after checkout.",
  },
  GLIDE_IS_ENABLED: {
    key: "glide-is-enabled",
    defaultValue: true,
    description:
      "Controls whether eligible users (fh_plus_mobile) are redirected to the Glide web app after account creation.",
  },
  HIDE_APPLE_FITNESS: {
    key: "hide_apple_fitness",
    defaultValue: false,
    description:
      "Hides the Apple Fitness card on Home when true. Default false preserves the previous LaunchDarkly fallback (unloaded ≠ hidden).",
  },
  LEAD_FORM_PHONE_FIELD_REQUIRED: {
    key: "lead-form-phone-field-required",
    defaultValue: false,
    description:
      "When enabled, the lead form phone field is required and the SMS opt-in is optional (legacy behavior).",
  },
  LEAD_FORM_EMAIL_DOMAIN_VALIDATION: {
    key: "lead-form-email-domain-validation",
    defaultValue: false,
    description:
      "MKTG-2485. Rejects lead form emails whose domain ends in a TLD that is not in the IANA list (e.g. gmail.con).",
  },
  SMD_MIGRATION_INTERCEPTION_ENABLED: {
    key: "smd-migration-interception-enabled",
    defaultValue: false,
    description:
      "Controls the FLEX-281 banner and medical-flow interception for the eligible SteadyMD Ionic cohort.",
  },
  HIDE_ZEPBOUND_KWIKPEN: {
    key: "hide-zepbound-kwikpen",
    defaultValue: false,
    description:
      "Hides the Zepbound KwikPen retake option on the deactivated-product fallback page when true. Default false preserves today's behavior while the flag is absent or unresolved.",
  },
  HIDE_WEGOVY_INJECTION: {
    key: "hide-wegovy-injection",
    defaultValue: false,
    description:
      "Hides the Wegovy Injection retake option on the deactivated-product fallback page when true. Default false preserves today's behavior while the flag is absent or unresolved.",
  },
  SELF_SERVE_MIGRATION_ENABLED: {
    key: "self-serve-migration-enabled",
    defaultValue: false,
    description:
      "Shows the self-serve migration CTA to eligible SMD users (MED-2674). Requires eligibility endpoint (MED-2670) and in_progress state (MED-2671) to be live before enabling.",
  },
} as const satisfies Record<string, PostHogFeatureFlagDefinition>;

type DefinitionMap = typeof POSTHOG_FEATURE_FLAG_DEFINITIONS;

export type PostHogFeatureFlagKey = DefinitionMap[keyof DefinitionMap]["key"];

export const POSTHOG_FEATURE_FLAGS = Object.freeze(
  Object.entries(POSTHOG_FEATURE_FLAG_DEFINITIONS).reduce(
    (acc, [definitionKey, definition]) => {
      acc[definitionKey as keyof DefinitionMap] = definition.key;
      return acc;
    },
    {} as Record<keyof DefinitionMap, PostHogFeatureFlagKey>
  )
);

export const POSTHOG_FEATURE_FLAG_DEFAULTS = Object.freeze(
  Object.values(POSTHOG_FEATURE_FLAG_DEFINITIONS).reduce(
    (acc, definition) => {
      acc[definition.key] = definition.defaultValue;
      return acc;
    },
    {} as Record<PostHogFeatureFlagKey, boolean>
  )
);

const POSTHOG_FEATURE_FLAG_KEY_SET = new Set<PostHogFeatureFlagKey>(
  Object.values(POSTHOG_FEATURE_FLAGS)
);

export const isPosthogFeatureFlagKey = (
  value: unknown
): value is PostHogFeatureFlagKey =>
  typeof value === "string" &&
  POSTHOG_FEATURE_FLAG_KEY_SET.has(value as PostHogFeatureFlagKey);
